/**
 * Native messaging / comment system — shared types and pure helpers.
 *
 * One thread belongs to exactly ONE parent entity, addressed by a generic
 * `EntityRef = { type, id }`. Adding a new commentable surface = add a string
 * to `EntityType` and register a resolver (backend) — no schema change.
 *
 * Everything here is pure and runtime-agnostic (Node + browser): the helpers
 * are the source of truth for entityKey normalization, pagination cursors,
 * reaction toggling, mention bounds, and unread comparison.
 */
/**
 * Open registry of entity kinds that can own a comment thread.
 * `product` is the internal product comment channel; `product_external` is the
 * separate external channel on the same product (shared with factory partners).
 * They are distinct entity keys, so each gets its own thread.
 */
/** Every entity a comment thread can hang off. The messaging route validates against this list. */
export const MESSAGING_ENTITY_TYPES = ['product', 'product_external', 'bug', 'submission'];
/** Allowlisted reaction emoji (v1). */
export const ALLOWED_REACTION_EMOJI = [
    '\u{1F44D}', // 👍
    '\u{1F44E}', // 👎
    '❤️', // ❤️
    '\u{1F604}', // 😄
    '\u{1F389}', // 🎉
    '\u{1F615}', // 😕
    '\u{1F680}', // 🚀
    '\u{1F440}', // 👀
];
/**
 * True iff `emoji` is in the v1 allowlist. NOTE: callers should NFC-normalize
 * the input first (e.g. `emoji.normalize('NFC')`) — the allowlist stores the
 * variation-selector form of ❤️, so an un-normalized bare heart is rejected.
 */
export function isAllowedEmoji(emoji) {
    return ALLOWED_REACTION_EMOJI.includes(emoji);
}
// Rejects control chars (0x00–0x1F), space (0x20), and DEL (0x7F). Space is
// disallowed in entity IDs because it makes socket room names and log greps
// ambiguous.
const CONTROL_CHARS_RE = /[\x00-\x20\x7F]/;
/**
 * Canonical entityKey for a parent entity. Server-trusted only.
 * `type` is lowercased; `id` is trimmed but case-preserved (IDs like BUG-123
 * are case-sensitive). The `:` delimiter is escaped inside `id`.
 */
export function normalizeEntityKey(type, id) {
    const t = type.trim().toLowerCase();
    if (!t)
        throw new Error('entityKey: empty type');
    const rawId = id.trim();
    if (!rawId)
        throw new Error('entityKey: empty id');
    if (CONTROL_CHARS_RE.test(rawId)) {
        throw new Error('entityKey: id contains control characters');
    }
    const escapedId = rawId.replace(/\\/g, '\\\\').replace(/:/g, '\\:');
    return `${t}:${escapedId}`;
}
/**
 * Client-side entityKey for store keying. Produces the SAME string as
 * `normalizeEntityKey` for valid refs, but never throws (returns a best-effort
 * key) so a transient bad ref can't crash a render. The server remains the
 * source of truth for the persisted key.
 */
export function normalizeEntityKeyClient(type, id) {
    try {
        return normalizeEntityKey(type, id);
    }
    catch {
        return `${String(type).trim().toLowerCase()}:${String(id).trim()}`;
    }
}
/** Encode an entityKey into a socket-room-safe segment (base64url, no padding). */
export function encodeEntityKey(entityKey) {
    const bytes = new TextEncoder().encode(entityKey);
    let bin = '';
    for (const b of bytes)
        bin += String.fromCharCode(b);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
/** Per-thread socket room name. */
export function getThreadRoom(entityKey) {
    return `thread:${encodeEntityKey(entityKey)}`;
}
/** Format an opaque pagination cursor from a message's (createdAt, _id). */
export function formatCursor(createdAtIso, id) {
    return `${createdAtIso}_${id}`;
}
/** Parse a cursor back into its parts. Returns null when malformed. */
export function parseCursor(cursor) {
    const idx = cursor.lastIndexOf('_');
    if (idx <= 0 || idx === cursor.length - 1)
        return null;
    return { createdAtIso: cursor.slice(0, idx), id: cursor.slice(idx + 1) };
}
/** Pure toggle: returns a NEW reactions array with `userId` flipped for `emoji`. */
export function toggleReaction(reactions, emoji, userId) {
    const next = reactions.map((g) => ({ emoji: g.emoji, userIds: [...g.userIds] }));
    const group = next.find((g) => g.emoji === emoji);
    if (!group) {
        next.push({ emoji, userIds: [userId] });
    }
    else if (group.userIds.includes(userId)) {
        group.userIds = group.userIds.filter((u) => u !== userId);
    }
    else {
        group.userIds.push(userId);
    }
    return next.filter((g) => g.userIds.length > 0);
}
/** True iff every mention's [offset, offset+length) fits inside `body`. */
export function mentionsWithinBody(body, mentions) {
    return mentions.every((m) => m.offset >= 0 && m.length > 0 && m.offset + m.length <= body.length);
}
/**
 * Is a message strictly newer than a read watermark? Uses (createdAt, _id) so
 * two messages in the same millisecond can't be miscounted. ObjectId hex
 * strings are monotonic and lexicographically comparable.
 */
export function isUnreadAgainstWatermark(msg, watermark) {
    if (!watermark.lastReadAt)
        return true;
    if (msg.createdAt > watermark.lastReadAt)
        return true;
    if (msg.createdAt < watermark.lastReadAt)
        return false;
    if (!watermark.lastReadMessageId)
        return true;
    return msg._id > watermark.lastReadMessageId;
}
//# sourceMappingURL=messaging.types.js.map